import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Icon, Text, VStack } from '@chakra-ui/react';
import { LuList } from 'react-icons/lu';
import { DocumentWithDownloadUrl as Document } from '@/client';
import CollapsibleCard from '@/components/common/CollapsibleCard';
import GeneratedByBundleNote from '@/components/common/GeneratedByBundleNote';
import { useDocumentStatus } from '@/hooks/house/documents/useDocumentStatus';
import ProcessingBadge, { ProcessingBadgeKind } from '@/components/common/ProcessingBadge';
import CompletionSwap from '@/components/common/CompletionSwap';

interface DocumentSummaryProps {
	document: Document;
	/** A pending share isn't in this house's pipeline; render its summary as-is, never as "Processing". */
	isPendingShare?: boolean;
}

/**
 * Displays the AI-generated document summary in a collapsible card.
 * Only renders when a summary is available on the document — or shows a Processing badge
 * while the enrichment pipeline is still running for the first time.
 */
const DocumentSummary: React.FC<DocumentSummaryProps> = ({ document, isPendingShare = false }) => {
	// The summary is produced by document_enrichment — gate on the enrichment path, not the generic
	// pipeline flag (which also tracks extraction/kbs and would mis-report the summary's readiness).
	// A pending share's document isn't in this house's status stream, so skip the lookup: its summary
	// comes from the share payload and must never read as perpetually "Processing".
	const { isEnrichmentPending } = useDocumentStatus(isPendingShare ? null : document.id, document.uploaded_at);
	const [isExpanded, setIsExpanded] = useState(false);
	const [showToggle, setShowToggle] = useState(false);
	const summaryRef = useRef<HTMLParagraphElement | null>(null);

	// Measure overflow before paint (useLayoutEffect, not useEffect) so the "View more" toggle is present
	// in the first frame instead of popping in a tick after the summary renders.
	useLayoutEffect(() => {
		const element = summaryRef.current;
		if (!element || !document.summary?.content) {
			setShowToggle(false);
			return;
		}

		setIsExpanded(false);
		const hasOverflow = element.scrollHeight > element.clientHeight + 1;
		setShowToggle(hasOverflow);
	}, [document.summary?.content]);

	if (!document.summary && !isEnrichmentPending) {
		return null;
	}

	return (
		<CollapsibleCard
			title="Summary"
			icon={<Icon as={LuList} boxSize={5} color="brand.fg" />}
			value="summary"
			defaultOpen={true}
		>
			<CompletionSwap
				pending={!document.summary && isEnrichmentPending}
				pendingContent={
					<Box pr={5} pb={2}>
						<ProcessingBadge kind={ProcessingBadgeKind.Summary} />
					</Box>
				}
			>
				{document.summary ? (
					<VStack align="stretch" gap={2}>
						<Box pr={5}>
							<Text
								ref={summaryRef}
								textStyle="body"
								color="brand.fg"
								lineHeight={1.625}
								lineClamp={isExpanded ? 'none' : 5}
							>
								{document.summary.content}
							</Text>
							{showToggle && (
								<Button
									variant="plain"
									size="sm"
									mt={2}
									px={0}
									minW="auto"
									height="auto"
									fontSize="sm"
									fontWeight="semibold"
									color="brand.fg"
									onClick={() => setIsExpanded((current) => !current)}
								>
									{isExpanded ? 'View less' : 'View more'}
								</Button>
							)}
						</Box>

						<Box pr={5}>
							<GeneratedByBundleNote label="Summary generated by Bundle" />
						</Box>
					</VStack>
				) : null}
			</CompletionSwap>
		</CollapsibleCard>
	);
};

export default DocumentSummary;
